"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { IconChevronDown } from "@/lib/tabler-icons";

/**
 * Contenido del banner: título, CTAs e indicador de scroll. Su HTML se renderiza
 * en servidor (SEO/LCP) y framer-motion solo añade la animación de entrada al hidratar.
 */
const BannerMotionContent = () => {
	const prefersReducedMotion = useReducedMotion();

	return (
	<div className="relative z-20 mt-16 text-center w-full flex flex-col items-center">
		<motion.div
			initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.8, type: "spring" }}
			className="text-xs tracking-[0.2em] uppercase text-cream/90 bg-ink/30 backdrop-blur-md rounded-full px-6 py-2 ring-1 ring-cream/20 border border-cream/20"
			aria-label="Asociación Equilibrio"
		>
			<div className="flex items-center gap-2">
				<div className="w-2 h-2 bg-terracotta rounded-full"></div>
				Asociación Equilibrio
				<div className="w-2 h-2 bg-terracotta rounded-full"></div>
			</div>
		</motion.div>
		<motion.h1
			className="font-display text-5xl sm:text-7xl font-semibold text-cream container mx-auto mt-6 leading-tight"
			initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 40 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 1.2, type: "spring" }}
		>
			<Link
				href="/asociacion-equilibrio/lidera"
				tabIndex={0}
				className="focus:outline-none focus:ring-2 focus:ring-cream/40 transition-all duration-200"
			>
				LIDERA TU PROYECTO
			</Link>
		</motion.h1>
		<p className="font-body mt-6 max-w-3xl text-cream/85 text-lg sm:text-xl px-6 leading-relaxed">
			Impulsamos iniciativas socioculturales con enfoque de derechos,
			participación y comunidad. Conecta tu idea con las personas a las que
			puede transformar.
		</p>
		<div className="h-px w-32 mx-auto bg-terracotta mt-8" />
		{/* CTA buttons */}
		<div className="mt-10 flex flex-col sm:flex-row gap-6">
			<Link
				href="#contacto"
				className="font-body px-8 py-4 rounded-full bg-terracotta text-cream font-semibold hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2 focus:ring-offset-ink border border-terracotta/60"
				aria-label="Proponer iniciativa sociocultural"
			>
				Proponer iniciativa
			</Link>
			<Link
				href="#ambitos"
				className="font-body px-8 py-4 rounded-full bg-cream/10 text-cream font-semibold border border-cream/40 backdrop-blur hover:bg-cream/20 hover:border-cream/60 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cream/50 focus:ring-offset-2 focus:ring-offset-ink"
				aria-label="Ver ámbitos de actuación"
			>
				Ver ámbitos
			</Link>
		</div>
		{/* Indicador de scroll */}
		<motion.div
			className="mt-12 flex flex-col items-center"
			initial={{ opacity: 0, y: prefersReducedMotion ? 0 : -10 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ delay: 1.5, duration: 0.8 }}
		>
			<div className="flex items-center gap-2 mb-3">
				<div className="w-1 h-1 bg-terracotta rounded-full"></div>
				<span className="font-body text-cream/80 text-sm">
					Desliza para descubrir
				</span>
				<div className="w-1 h-1 bg-terracotta rounded-full"></div>
			</div>
			<div className="p-2 rounded-full bg-cream/10 backdrop-blur border border-cream/20 animate-bounce">
				<IconChevronDown
					className="text-cream/80"
					size={24}
					aria-hidden="true"
				/>
			</div>
		</motion.div>
	</div>
	);
};

export default BannerMotionContent;
